// Names over emails, everywhere a person is shown.
//
// One map, email (or login) -> display name, built once per page load from
// the three places Nexus already knows people: the work directory (Nexus
// superusers + the admin People directory, including each entry's aliases),
// the Azure DevOps roster and the Monday seats. Unknown values are shown as
// they are -- a free-text name, a district contact's email -- because
// "where possible" means never inventing a name.

import { useEffect, useMemo, useState } from 'react';
import { getStaffDirectory, getAdoAssignees, getMondayPeople } from './workService';

let names = new Map();
const usernames = new Map();
let loading = null;
const listeners = new Set();

const add = (email, name) => {
    const e = String(email || '').trim().toLowerCase();
    const n = String(name || '').trim();
    // A "name" that is itself an email tells us nothing.
    if (e && n && !n.includes('@') && !names.has(e)) names.set(e, n);
};

export function loadPeopleNames() {
    if (!loading) {
        const call = (fn) => { try { return typeof fn === 'function' ? fn() : []; } catch { return []; } };
        loading = Promise.allSettled([call(getStaffDirectory), call(getAdoAssignees), call(getMondayPeople)])
            .then(([staff, ado, monday]) => {
                const next = new Map(names);
                names = next;
                // The admin directory and superusers first: they are the names
                // people chose; ADO and Monday fill the rest.
                for (const p of staff.value || []) {
                    add(p.email, p.name);
                    for (const a of p.aliases || []) add(a, p.name);
                }
                for (const p of ado.value || []) add(p.email, p.name);
                for (const p of monday.value || []) add(p.email, p.name);
                // Bare lowercase usernames ("patrick" in RequestedBy/CreatedBy,
                // from the login's preferred username) resolve to the one
                // person whose email has that local part. Only when exactly
                // one does, and only lowercase: "Robert" typed as a district
                // contact must not become Robert Griffith.
                const byLocal = new Map();
                for (const [email, name] of names) {
                    if (!email.includes('@')) continue;
                    const local = email.split('@')[0];
                    const cur = byLocal.get(local);
                    byLocal.set(local, cur === undefined || cur === name ? name : null);
                }
                for (const [local, name] of byLocal) {
                    if (name && !names.has(local)) usernames.set(local, name);
                }
                listeners.forEach((fn) => fn(names));
                return names;
            });
    }
    return loading;
}

/** The display name for an email or login, else the value unchanged. */
export function personName(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const hit = names.get(raw.toLowerCase());
    if (hit) return hit;
    if (!raw.includes('@') && raw === raw.toLowerCase()) return usernames.get(raw) || raw;
    return raw;
}

/** An attendee {name, email}: the directory's name for the email, else the
 *  calendar's name unless that is itself just an email. */
export function attendeeName(p) {
    const byEmail = personName(p?.email);
    if (p?.email && byEmail !== p.email) return byEmail;
    return personName(p?.name) || p?.email || '';
}

/** Re-renders the caller once names arrive; returns personName. */
export function usePeopleNames() {
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const fn = () => setTick((t) => t + 1);
        listeners.add(fn);
        loadPeopleNames().catch(() => {});
        return () => listeners.delete(fn);
    }, []);
    // A new function each time names arrive, so memoised lists recompute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => (v) => personName(v), [tick]);
}

/** Test hook. */
export function __setPeopleNames(entries) {
    names = new Map(Object.entries(entries).map(([k, v]) => [k.toLowerCase(), v]));
    usernames.clear();
    for (const [k, v] of names) if (k.includes('@')) usernames.set(k.split('@')[0], v);
    loading = Promise.resolve(names);
    listeners.forEach((fn) => fn(names));
}
